import React, { useEffect, useState } from "react";
import { MapPin, Loader2, Navigation } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";

const SESSION_KEY = "pg_user_location_v1";

/**
 * UseMyLocationButton
 * One-tap chip that asks the browser for the user's coordinates, reverse-
 * geocodes to a human-readable label via Nominatim (free), and reports
 * back to the parent with `{ lat, lng, label }`.
 *
 * Caches the result in sessionStorage so subsequent taps within the same
 * tab return instantly. The browser handles the actual permission prompt.
 *
 * Renders nothing if the runtime lacks geolocation support (very rare).
 */
const UseMyLocationButton = ({ onLocated, className = "" }) => {
  const [supported, setSupported] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSupported(typeof navigator !== "undefined" && !!navigator.geolocation);
  }, []);

  if (!supported) return null;

  const reverseGeocode = async (lat, lng) => {
    // Nominatim usage policy requires a real referer / no high-frequency
    // hammering — we only fire once per user tap, well within limits.
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=12&addressdetails=1`;
      const res = await fetch(url, {
        headers: { "Accept-Language": navigator.language || "en" },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const a = data?.address || {};
      // Prefer the most specific human-friendly label, in order:
      // neighbourhood/suburb → city/town/village → county → state
      const primary =
        a.neighbourhood ||
        a.suburb ||
        a.city ||
        a.town ||
        a.village ||
        a.hamlet ||
        a.county ||
        a.state ||
        "";
      const region = a.state || a.country || "";
      if (!primary) return null;
      return region && region !== primary ? `${primary}, ${region}` : primary;
    } catch {
      return null;
    }
  };

  const handleClick = () => {
    // Cache hit — instant, no permission prompt needed.
    try {
      const cached = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
      if (cached && Number.isFinite(cached.lat) && Number.isFinite(cached.lng)) {
        track("use_my_location", { source: "cache" });
        onLocated?.(cached);
        return;
      }
    } catch {
      /* ignore parse error */
    }

    // Insecure-context check. Mobile browsers (and Chrome since v50) refuse
    // geolocation on plain http://. If somehow this loads over http we
    // surface a clearer error than the cryptic PERMISSION_DENIED Safari
    // would otherwise emit. pawground.app is HTTPS so this should never
    // fire in production — useful for local dev / weird iframe setups.
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      toast.error("Location needs a secure (https://) connection.");
      track("use_my_location_error", { code: "insecure_context" });
      return;
    }

    // NOTE: we intentionally do NOT pre-check via
    // `navigator.permissions.query({ name: "geolocation" })`. iOS Safari
    // returns inconsistent / stale state from that API (state="denied"
    // when the browser-level setting is actually "Allow"), which would
    // block users who really do have permission. The real
    // `getCurrentPosition` call is the source of truth for whether the
    // user is permitted — error code 1 from THERE is the only signal we
    // trust to surface a "denied" message.

    setLoading(true);
    track("use_my_location", { source: "request" });

    // We use numeric error codes directly (1=PERMISSION_DENIED,
    // 2=POSITION_UNAVAILABLE, 3=TIMEOUT) rather than reading the
    // PERMISSION_DENIED constant off the error instance. Both are spec-
    // compliant but a few mobile browsers expose the constants only on
    // the prototype, and a defensive numeric compare can't drift.
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const label = (await reverseGeocode(lat, lng)) || "Your location";
        const payload = { lat, lng, label };
        try {
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
        } catch {
          /* private mode / storage disabled — fine, just no cache */
        }
        track("use_my_location_success", { label });
        onLocated?.(payload);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        track("use_my_location_error", { code: err.code, message: err.message });
        // Numeric codes per the W3C Geolocation spec:
        //   1 → PERMISSION_DENIED  (user / browser blocked)
        //   2 → POSITION_UNAVAILABLE (GPS off, no cell tower, etc.)
        //   3 → TIMEOUT (didn't return in time)
        // We branch on the number directly so a buggy mobile browser
        // that doesn't expose the static error constants still routes
        // correctly. Anything else (rare) falls into the generic case.
        if (err.code === 1) {
          toast.error(
            "Location denied. Check your browser's location permission for this site and try again."
          );
        } else if (err.code === 2) {
          toast.error(
            "Location unavailable. Make sure location services are turned on for your device."
          );
        } else if (err.code === 3) {
          toast.error("Location timed out, try again.");
        } else {
          toast.error("Couldn't detect your location. Try searching manually.");
        }
      },
      {
        // Mobile Safari benchmarks: high-accuracy can take 15-30s while
        // it waits for a GPS lock. We stay on low accuracy (wifi+cell
        // triangulation, ~50m on phones) — plenty for "what's nearby"
        // and resolves in 1-3s typically.
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 5 * 60 * 1000, // accept a 5-min-old fix
      }
    );
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      data-testid="use-my-location-btn"
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-brand-cream text-brand-forest border border-brand-forest/30 hover:bg-brand-light-green hover:border-brand-forest transition-all active:scale-95 disabled:opacity-70 disabled:cursor-wait ${className}`}
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Finding your location…</span>
        </>
      ) : (
        <>
          <Navigation className="w-4 h-4" />
          <span>Use my current location</span>
        </>
      )}
    </button>
  );
};

export default UseMyLocationButton;
